import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Stage } from './swimlanes/kanban/types'

export interface StageDeletionModalProps {
	isOpen: boolean
	onClose: () => void
	stageName: string
	candidateCount: number
	targetStages: Stage[]
	candidateCounts: Record<string, number>
	onConfirm: (migrateTo: string) => void
	isDeleting?: boolean
}

export function StageDeletionModal({
	isOpen,
	onClose,
	stageName,
	candidateCount,
	targetStages,
	candidateCounts,
	onConfirm,
	isDeleting = false,
}: StageDeletionModalProps) {
	const { t } = useTranslation('common')
	const [selectedTarget, setSelectedTarget] = useState<string>('')
	const selectRef = useRef<HTMLSelectElement>(null)
	const headingId = useId()
	const descriptionId = useId()

	// Reset selectedTarget when modal opens
	useEffect(() => {
		if (isOpen) {
			setSelectedTarget('')
		}
	}, [isOpen])

	// Focus select when modal opens
	useEffect(() => {
		if (isOpen && selectRef.current) {
			selectRef.current.focus()
		}
	}, [isOpen])

	// Handle Escape key to close modal
	useEffect(() => {
		if (!isOpen) { return }

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !isDeleting) {
				onClose()
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [isOpen, isDeleting, onClose])

	if (!isOpen) { return null }

	const handleConfirm = () => {
		if (selectedTarget) {
			onConfirm(selectedTarget)
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-black/50"
				onClick={onClose}
			/>

			{/* Modal */}
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby={headingId}
				aria-describedby={descriptionId}
				className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
			>
				<h2 id={headingId} className="text-lg font-semibold text-gray-900 mb-2">
					Delete "{stageName}" stage?
				</h2>

				<p id={descriptionId} className="text-sm text-gray-600 mb-4">
					This stage has{' '}
					<span className="font-medium">{candidateCount} candidate{candidateCount !== 1 ? 's' : ''}</span>.
					Choose where to move them:
				</p>

				<select
					ref={selectRef}
					value={selectedTarget}
					onChange={(e) => setSelectedTarget(e.target.value)}
					className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 mb-4"
					disabled={isDeleting}
				>
					<option value="">{t('pipeline.selectStage')}</option>
					{targetStages.map((stage) => {
						const count = candidateCounts[stage.id] ?? 0
						return (
							<option key={stage.id} value={stage.id}>
								{stage.name} ({count} {count === 1 ? 'candidate' : 'candidates'})
							</option>
						)
					})}
				</select>

				<div className="flex gap-3 justify-end">
					<button
						type="button"
						onClick={onClose}
						disabled={isDeleting}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleConfirm}
						disabled={!selectedTarget || isDeleting}
						className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
					>
						{isDeleting && (
							<svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
									fill="none"
								/>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
						)}
						Delete & Move
					</button>
				</div>
			</div>
		</div>
	)
}
